/**
 * Hook for managing pending confirmation actions in the AI chat.
 * Handles accept/reject flows for requestConfirmation tool calls.
 */

import { useCallback, useEffect, useState } from 'react';
import type { UIMessage } from 'ai';

import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';

interface PendingAction {
  actionId: string;
  message: string;
}

interface UsePendingChatActionResult {
  pendingAction: PendingAction | null;
  isExecuting: boolean;
  handleAccept: () => Promise<void>;
  handleReject: () => Promise<void>;
  clearPendingAction: () => void;
}

export function usePendingChatAction(
  messages: UIMessage[],
  sendChatMessage: (msg: { text: string }) => Promise<void>,
  setUsageLimitError: (error: string | null) => void,
): UsePendingChatActionResult {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  // Detect pending actions from messages
  useEffect(() => {
    if (messages.length === 0) return;

    const latestMessage = messages[messages.length - 1];
    if (latestMessage.role !== 'assistant') return;

    for (const part of latestMessage.parts) {
      if (
        part.type === 'tool-result' &&
        (part as any).toolName === 'requestConfirmation'
      ) {
        const output = (part as any).output;
        if (output?.requiresConfirmation && output?.actionId) {
          setPendingAction({
            actionId: output.actionId,
            message: output.summary || 'Confirmation required',
          });
          break;
        }
      }
    }
  }, [messages]);

  const handleAccept = useCallback(async () => {
    if (!pendingAction || isExecuting) return;

    setIsExecuting(true);

    try {
      const response = await authenticatedFetch.fetch('/api/chat/execute-pending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionId: pendingAction.actionId }),
      });

      const result = await response.json();

      if (result.success) {
        await sendChatMessage({ text: 'Confirmed - action executed successfully.' });
        setPendingAction(null);
      } else {
        console.error('[accept-action]', result.error);
        setUsageLimitError(result.error || 'Failed to execute action');
      }
    } catch (error) {
      console.error('[accept-action]', error);
      setUsageLimitError('Failed to execute action');
    } finally {
      setIsExecuting(false);
    }
  }, [pendingAction, isExecuting, sendChatMessage, setUsageLimitError]);

  const handleReject = useCallback(async () => {
    if (!pendingAction || isExecuting) return;

    setIsExecuting(true);

    try {
      const response = await authenticatedFetch.fetch('/api/chat/reject-pending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionId: pendingAction.actionId }),
      });

      const result = await response.json();

      if (result.success) {
        await sendChatMessage({ text: 'Action cancelled.' });
        setPendingAction(null);
      } else {
        console.error('[reject-action]', result.message);
      }
    } catch (error) {
      console.error('[reject-action]', error);
    } finally {
      setIsExecuting(false);
    }
  }, [pendingAction, isExecuting, sendChatMessage]);

  const clearPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  return {
    pendingAction,
    isExecuting,
    handleAccept,
    handleReject,
    clearPendingAction,
  };
}
